/**
 * Cart Checkout Modal
 * Multi-step checkout flow that adapts based on product type:
 * - Physical products: Customer info → Shipping address → Payment
 * - Digital/Event/Subscription: Customer info → Payment
 */

import type { ApiClient } from '../../utils/api-client';
import type { CartManager } from '../../utils/cart-manager';
import { cartLineVariantIds } from '../../utils/cart-manager';
import type { Product, ProductVariant } from '../../types';
import { OVERLAY_TOKENS } from '../theme';

export interface CartCheckoutModalOptions {
  api: ApiClient;
  cartManager?: CartManager;
  merchantId: string | number;
  currency: string;
  /** For direct checkout of non-physical products (bypasses cart) */
  singleProduct?: {
    product: Product;
    quantity: number;
    variant?: ProductVariant;
  };
  onSuccess: (result: {
    sessionToken: string;
    orderId?: string;
    amount: number;
    currency: string;
    paymentMethod?: 'stripe' | 'paystack';
  }) => void;
  onError: (error: Error) => void;
  onClose: () => void;
}

interface CustomerInfo {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

interface ShippingAddress {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode: string;
}

interface CheckoutState {
  step: 'customer' | 'shipping' | 'payment';
  customer: CustomerInfo | null;
  shipping: ShippingAddress | null;
  cartUniqueId: string | null;
  shippingFee: number;
  totalWithShipping: number;
  /** Khaime transaction fee in cents. 0 when the merchant absorbs it. */
  transactionFee: number;
  /** Whether the merchant has customer_pays_transaction_fee enabled. */
  customerPaysFees: boolean;
  isLoading: boolean;
  error: string | null;
}

const MODAL_STYLES = `
  .ks-checkout-modal-overlay {
    ${OVERLAY_TOKENS}
    position: fixed;
    inset: 0;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: rgba(17, 24, 39, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    font-family: var(--kso-font);
    color: var(--kso-ink);
    opacity: 0;
    transition: opacity 220ms ease;
  }

  .ks-checkout-modal-overlay * { box-sizing: border-box; }

  .ks-checkout-modal-overlay.ks-visible { opacity: 1; }

  .ks-checkout-modal {
    background: var(--kso-surface);
    border-radius: var(--kso-radius-md);
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
    overflow-x: hidden;
    box-shadow: var(--kso-shadow);
    transform: translateY(8px) scale(0.98);
    transition: transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .ks-checkout-modal-overlay.ks-visible .ks-checkout-modal {
    transform: translateY(0) scale(1);
  }

  .ks-checkout-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.125rem 1.5rem;
    border-bottom: 1px solid var(--kso-hairline);
  }

  .ks-checkout-title {
    font-family: var(--kso-font-display);
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--kso-ink);
    margin: 0;
    letter-spacing: -0.01em;
  }

  .ks-checkout-close {
    background: none;
    border: none;
    padding: 0.5rem;
    margin: -0.5rem;
    cursor: pointer;
    color: var(--kso-muted);
    border-radius: 999px;
    transition: color 180ms ease, background 180ms ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ks-checkout-close:hover {
    color: var(--kso-ink);
    background: var(--kso-hairline);
  }

  .ks-checkout-body {
    padding: 1.25rem 1.5rem 1.5rem;
    overflow-x: hidden;
  }

  /* Step indicator */
  .ks-checkout-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  .ks-checkout-step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ks-checkout-step-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    border: 1.5px solid var(--kso-hairline-strong);
    color: var(--kso-muted-2);
    background: var(--kso-surface);
    flex-shrink: 0;
    transition: border-color 220ms ease, color 220ms ease, background 220ms ease;
  }

  .ks-checkout-step.active .ks-checkout-step-circle {
    border-color: var(--kso-accent);
    color: var(--kso-accent);
  }

  .ks-checkout-step.completed .ks-checkout-step-circle {
    border-color: var(--kso-accent);
    background: var(--kso-accent);
    color: var(--kso-accent-ink);
  }

  .ks-checkout-step-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--kso-muted-2);
    letter-spacing: -0.005em;
  }

  .ks-checkout-step.active .ks-checkout-step-label {
    color: var(--kso-accent);
    font-weight: 600;
  }

  .ks-checkout-step.completed .ks-checkout-step-label {
    color: var(--kso-ink);
  }

  .ks-checkout-step-line {
    width: 32px;
    height: 1.5px;
    background: var(--kso-hairline);
    margin: 0 0.5rem;
    flex-shrink: 1;
    min-width: 16px;
    border-radius: 2px;
  }

  .ks-checkout-step-line.completed { background: var(--kso-accent); }

  /* Order summary */
  .ks-checkout-summary {
    background: var(--kso-surface-alt);
    border: 1px solid var(--kso-hairline);
    border-radius: var(--kso-radius-xs);
    padding: 1rem;
    margin-bottom: 1.25rem;
  }

  .ks-checkout-items {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .ks-checkout-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .ks-checkout-item-img,
  .ks-checkout-item-placeholder {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    flex-shrink: 0;
    background: var(--kso-hairline);
  }

  .ks-checkout-item-img { object-fit: cover; }

  .ks-checkout-item-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--kso-muted-2);
    text-transform: uppercase;
    font-family: var(--kso-font-display);
  }

  .ks-checkout-item-info {
    flex: 1;
    min-width: 0;
  }

  .ks-checkout-item-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--kso-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ks-checkout-item-meta {
    font-size: 0.75rem;
    color: var(--kso-muted);
    margin-top: 2px;
  }

  .ks-checkout-item-price {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--kso-ink);
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  .ks-checkout-divider {
    height: 1px;
    background: var(--kso-hairline);
    margin: 0.75rem 0;
  }

  .ks-checkout-summary-row {
    display: flex;
    justify-content: space-between;
    padding: 0.125rem 0;
    font-size: 0.8125rem;
    color: var(--kso-muted);
  }

  .ks-checkout-summary-row.total {
    font-weight: 700;
    color: var(--kso-ink);
    font-size: 0.9375rem;
    padding-top: 0.375rem;
    font-variant-numeric: tabular-nums;
  }

  /* Form */
  .ks-checkout-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .ks-checkout-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.875rem;
  }

  .ks-checkout-field {
    display: flex;
    flex-direction: column;
    gap: 0.3125rem;
  }

  .ks-checkout-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--kso-ink-soft);
    letter-spacing: -0.005em;
  }

  .ks-checkout-input,
  .ks-checkout-select {
    padding: 0.6875rem 0.875rem;
    border: 1px solid var(--kso-hairline-strong);
    border-radius: var(--kso-radius-xs);
    font-size: 0.9375rem;
    font-family: inherit;
    color: var(--kso-ink);
    background: var(--kso-surface);
    outline: none;
    transition: border-color 180ms ease, box-shadow 180ms ease;
  }

  .ks-checkout-input::placeholder {
    color: var(--kso-muted-2);
  }

  .ks-checkout-select { cursor: pointer; }

  .ks-checkout-input:focus,
  .ks-checkout-select:focus {
    border-color: var(--kso-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--kso-accent) 16%, transparent);
  }

  .ks-checkout-input.error {
    border-color: var(--kso-danger);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--kso-danger) 14%, transparent);
  }

  .ks-checkout-error {
    font-size: 0.75rem;
    color: var(--kso-danger);
    margin-top: 0.125rem;
  }

  .ks-checkout-submit {
    padding: 0.9375rem 1.5rem;
    background: var(--kso-accent);
    color: var(--kso-accent-ink);
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: filter 220ms ease, transform 120ms ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.375rem;
  }

  .ks-checkout-submit:hover:not(:disabled) { filter: brightness(1.08); }
  .ks-checkout-submit:active:not(:disabled) { transform: scale(0.985); }

  .ks-checkout-submit:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .ks-checkout-back {
    padding: 0.5rem 0;
    background: transparent;
    color: var(--kso-muted);
    border: none;
    font-size: 0.8125rem;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    transition: color 180ms ease;
    text-align: center;
    margin-top: 0.25rem;
  }

  .ks-checkout-back:hover { color: var(--kso-ink); }

  .ks-checkout-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  @keyframes ks-spin {
    to { transform: rotate(360deg); }
  }

  .ks-checkout-global-error {
    padding: 0.75rem 1rem;
    background: var(--kso-danger-bg);
    border: 1px solid var(--kso-danger-border);
    border-radius: var(--kso-radius-xs);
    color: var(--kso-danger-ink);
    font-size: 0.8125rem;
    margin-bottom: 1rem;
  }

  .ks-payment-processing {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 2rem;
    gap: 1rem;
    text-align: center;
  }

  .ks-payment-processing-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--kso-hairline);
    border-top-color: var(--kso-accent);
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  .ks-payment-processing-text {
    color: var(--kso-muted);
    font-size: 0.875rem;
  }

  /* Responsive — full-screen on narrow viewports. Safe to use @media here
     because the modal overlays the viewport itself, not a WP content column. */
  @media (max-width: 480px) {
    .ks-checkout-modal-overlay { padding: 0; }
    .ks-checkout-modal {
      max-width: 100%;
      max-height: 100%;
      height: 100%;
      border-radius: 0;
    }
    .ks-checkout-row { grid-template-columns: 1fr; }
  }
`;

// Country list with codes
const COUNTRIES = [
  { name: 'United States', code: 'US' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Canada', code: 'CA' },
  { name: 'Australia', code: 'AU' },
  { name: 'Germany', code: 'DE' },
  { name: 'France', code: 'FR' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Kenya', code: 'KE' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'India', code: 'IN' },
  { name: 'Brazil', code: 'BR' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Japan', code: 'JP' },
  { name: 'China', code: 'CN' },
  { name: 'Spain', code: 'ES' },
  { name: 'Italy', code: 'IT' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Singapore', code: 'SG' },
  { name: 'United Arab Emirates', code: 'AE' },
].sort((a, b) => a.name.localeCompare(b.name));

export async function openCartCheckoutModal(options: CartCheckoutModalOptions): Promise<void> {
  const { api, cartManager, merchantId, currency, singleProduct, onSuccess, onError, onClose } = options;

  // Single-product mode (direct checkout for non-physical products)
  const isSingleProductMode = !!singleProduct;

  // Derive cart-like data from either cart or single product
  const singleProductAny = singleProduct?.product as any;
  const hasEventTickets = singleProductAny?.tickets && Array.isArray(singleProductAny.tickets) && singleProductAny.tickets.length > 0;

  // For live events with tickets, use the ticket total as the price
  let singleProductPrice = singleProduct?.variant?.price
    || (singleProduct?.product.is_slash_price && singleProduct?.product.slash_price ? singleProduct?.product.slash_price : singleProduct?.product.price)
    || 0;

  // Subscription: use selected tier price if available
  const selectedFreqKey = singleProductAny?.selected_frequency_key as string | undefined;
  if (selectedFreqKey && singleProduct?.product.subscription_options?.pricing_options) {
    const tier = singleProduct.product.subscription_options.pricing_options.find(
      (o: any) => o.value === selectedFreqKey
    );
    if (tier?.final_price != null) {
      singleProductPrice = tier.final_price;
    }
  }

  if (hasEventTickets && singleProduct?.product.event?.tickets) {
    // Calculate total from selected tickets
    let ticketTotal = 0;
    for (const sel of singleProductAny.tickets) {
      const ticketDef = singleProduct.product.event.tickets.find((t: any) => t.id === sel.ticket_id);
      if (ticketDef) ticketTotal += ticketDef.price * sel.quantity;
    }
    if (ticketTotal > 0) singleProductPrice = ticketTotal;
  }

  // Use the product's own currency (reflects actual conversion state) for display,
  // but keep the storefront currency for API calls
  const displayCurrency = isSingleProductMode
    ? (singleProduct!.product.currency || currency)
    : currency;

  const items = isSingleProductMode
    ? [{
        product_id: singleProduct!.product.id,
        title: singleProduct!.product.title,
        thumbnail: singleProduct!.product.thumbnail || null,
        type: singleProduct!.product.type,
        quantity: singleProduct!.quantity,
        price: singleProductPrice,
        variant_id: singleProduct!.variant?.id,
        main_variant_id: singleProduct!.variant?.variation_id,
        variant_name: singleProduct!.variant?.name,
        tickets: hasEventTickets ? singleProductAny.tickets : undefined,
        subscription_frequency_key: selectedFreqKey,
        currency: displayCurrency,
        added_at: Date.now(),
      }]
    : cartManager!.getItems();

  const subtotal = isSingleProductMode
    ? items[0].price * items[0].quantity
    : cartManager!.getSubtotalMinor();

  const cartId = isSingleProductMode
    ? `direct_${Date.now()}`
    : cartManager!.getCartId();

  const itemCount = isSingleProductMode
    ? singleProduct!.quantity
    : cartManager!.getItemCount();

  const productType = isSingleProductMode
    ? singleProduct!.product.type
    : cartManager!.getProductType();

  // Single-product mode never requires shipping (only non-physical products use it)
  const requiresShipping = isSingleProductMode ? false : cartManager!.requiresShipping();

  // Checkout state
  const state: CheckoutState = {
    step: 'customer',
    customer: null,
    shipping: null,
    cartUniqueId: null,
    shippingFee: 0,
    totalWithShipping: subtotal,
    transactionFee: 0,
    customerPaysFees: false,
    isLoading: false,
    error: null,
  };

  /**
   * Format an amount in minor units (cents/kobo) as a display string.
   * Distinct from the SDK-global `formatPrice` which expects major units —
   * keeping them separate prevents the whole modal from accidentally
   * double-dividing by 100. Inline symbol map is used so the checkout
   * doesn't depend on `Intl.NumberFormat` locale data for NGN/GHS/etc.
   */
  const formatCents = (amountInCents: number, curr: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      NGN: '₦',
      GHS: 'GH₵',
      KES: 'KSh',
      ZAR: 'R',
      RWF: 'RF',
      TZS: 'TSh',
      UGX: 'USh',
      XOF: 'CFA',
    };
    const symbol = symbols[curr] || curr + ' ';
    const amount = amountInCents / 100; // Convert cents to dollars
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get step title based on product type
  const getStepTitle = () => {
    switch (state.step) {
      case 'customer':
        return 'Customer Information';
      case 'shipping':
        return 'Shipping Address';
      case 'payment':
        return 'Payment';
      default:
        return 'Checkout';
    }
  };

  // Create modal
  const overlay = document.createElement('div');
  overlay.className = 'ks-checkout-modal-overlay';

  function render() {
    const steps = requiresShipping ? ['customer', 'shipping', 'payment'] : ['customer', 'payment'];

    const currentStepIndex = steps.indexOf(state.step);

    overlay.innerHTML = `
      <style>${MODAL_STYLES}</style>
      <div class="ks-checkout-modal">
        <header class="ks-checkout-header">
          <h3 class="ks-checkout-title">${getStepTitle()}</h3>
          <button class="ks-checkout-close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div class="ks-checkout-body">
          ${renderSteps(steps, currentStepIndex)}
          ${renderSummary()}
          ${state.error ? `<div class="ks-checkout-global-error">${state.error}</div>` : ''}
          ${renderCurrentStep()}
        </div>
      </div>
    `;

    attachEventListeners();
  }

  function renderSteps(steps: string[], currentIndex: number): string {
    const checkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>`;

    return `
      <div class="ks-checkout-steps">
        ${steps
          .map((step, index) => {
            const isActive = index === currentIndex;
            const isCompleted = index < currentIndex;
            const className = isActive ? 'active' : isCompleted ? 'completed' : '';
            const label =
              step === 'customer' ? 'Info' : step === 'shipping' ? 'Shipping' : 'Payment';
            const circleContent = isCompleted ? checkSvg : `${index + 1}`;
            const lineDone = index < currentIndex;

            return `
            <div class="ks-checkout-step ${className}">
              <span class="ks-checkout-step-circle">${circleContent}</span>
              <span class="ks-checkout-step-label">${label}</span>
            </div>
            ${index < steps.length - 1 ? `<div class="ks-checkout-step-line ${lineDone ? 'completed' : ''}"></div>` : ''}
          `;
          })
          .join('')}
      </div>
    `;
  }

  function renderSummary(): string {
    const showShipping = requiresShipping && state.step !== 'customer';
    const total = showShipping ? state.totalWithShipping : subtotal;

    const renderItemImage = (item: typeof items[0]) => {
      const initial = (item.title || '?').charAt(0).toUpperCase();
      if (item.thumbnail) {
        return `<img class="ks-checkout-item-img" src="${item.thumbnail}" alt="" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" /><div class="ks-checkout-item-placeholder" style="display:none;">${initial}</div>`;
      }
      return `<div class="ks-checkout-item-placeholder">${initial}</div>`;
    };

    const itemRows = items.map(item => {
      const metaParts: string[] = [];
      if (item.variant_name) metaParts.push(item.variant_name);
      if (item.quantity > 1) metaParts.push(`Qty: ${item.quantity}`);
      else metaParts.push('Qty: 1');
      const meta = metaParts.join(' · ');
      const linePrice = item.price * item.quantity;

      return `
        <div class="ks-checkout-item">
          ${renderItemImage(item)}
          <div class="ks-checkout-item-info">
            <div class="ks-checkout-item-title">${item.title}</div>
            <div class="ks-checkout-item-meta">${meta}</div>
          </div>
          <div class="ks-checkout-item-price">${formatCents(linePrice, displayCurrency)}</div>
        </div>
      `;
    }).join('');

    // When the merchant has customer_pays_transaction_fee enabled, the Khaime
    // fee is added on top of subtotal+shipping on the server side. Show it as
    // its own row so the displayed Total matches what the customer will be
    // charged.
    const showFee = state.customerPaysFees && state.transactionFee > 0;
    const grandTotal = showFee ? total + state.transactionFee : total;

    return `
      <div class="ks-checkout-summary">
        <div class="ks-checkout-items">
          ${itemRows}
        </div>
        <div class="ks-checkout-divider"></div>
        <div class="ks-checkout-summary-row">
          <span>Subtotal</span>
          <span>${formatCents(subtotal, displayCurrency)}</span>
        </div>
        ${
          showShipping && state.shippingFee > 0
            ? `
          <div class="ks-checkout-summary-row">
            <span>Shipping</span>
            <span>${formatCents(state.shippingFee, displayCurrency)}</span>
          </div>
        `
            : ''
        }
        ${
          showFee
            ? `
          <div class="ks-checkout-summary-row">
            <span>Transaction Fee</span>
            <span>${formatCents(state.transactionFee, displayCurrency)}</span>
          </div>
        `
            : ''
        }
        <div class="ks-checkout-summary-row total">
          <span>Total</span>
          <span>${formatCents(grandTotal, displayCurrency)}</span>
        </div>
      </div>
    `;
  }

  function renderCurrentStep(): string {
    switch (state.step) {
      case 'customer':
        return renderCustomerForm();
      case 'shipping':
        return renderShippingForm();
      case 'payment':
        return renderPaymentProcessing();
      default:
        return '';
    }
  }

  function renderCustomerForm(): string {
    return `
      <form class="ks-checkout-form" id="customer-form">
        <div class="ks-checkout-field">
          <label class="ks-checkout-label" for="ks-email">Email *</label>
          <input type="email" id="ks-email" class="ks-checkout-input" placeholder="your@email.com" required
            value="${state.customer?.email || ''}" />
        </div>
        <div class="ks-checkout-row">
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-first-name">First Name *</label>
            <input type="text" id="ks-first-name" class="ks-checkout-input" placeholder="John" required
              value="${state.customer?.firstName || ''}" />
          </div>
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-last-name">Last Name *</label>
            <input type="text" id="ks-last-name" class="ks-checkout-input" placeholder="Doe" required
              value="${state.customer?.lastName || ''}" />
          </div>
        </div>
        <div class="ks-checkout-field">
          <label class="ks-checkout-label" for="ks-phone">Phone *</label>
          <input type="tel" id="ks-phone" class="ks-checkout-input" placeholder="+1 234 567 8900"
            value="${state.customer?.phone || ''}" />
        </div>
        <button type="submit" class="ks-checkout-submit" ${state.isLoading ? 'disabled' : ''}>
          ${
            state.isLoading
              ? '<span class="ks-checkout-spinner"></span> Processing...'
              : requiresShipping
                ? 'Continue to Shipping \u2192'
                : 'Continue to Payment \u2192'
          }
        </button>
      </form>
    `;
  }

  function renderShippingForm(): string {
    return `
      <form class="ks-checkout-form" id="shipping-form">
        <div class="ks-checkout-field">
          <label class="ks-checkout-label" for="ks-ship-name">Full Name *</label>
          <input type="text" id="ks-ship-name" class="ks-checkout-input" placeholder="John Doe" required
            value="${state.shipping?.name || `${state.customer?.firstName || ''} ${state.customer?.lastName || ''}`.trim()}" />
        </div>
        <div class="ks-checkout-field">
          <label class="ks-checkout-label" for="ks-address">Street Address *</label>
          <input type="text" id="ks-address" class="ks-checkout-input" placeholder="123 Main St" required
            value="${state.shipping?.address || ''}" />
        </div>
        <div class="ks-checkout-row">
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-city">City *</label>
            <input type="text" id="ks-city" class="ks-checkout-input" placeholder="New York" required
              value="${state.shipping?.city || ''}" />
          </div>
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-state">State/Province *</label>
            <input type="text" id="ks-state" class="ks-checkout-input" placeholder="NY" required
              value="${state.shipping?.state || ''}" />
          </div>
        </div>
        <div class="ks-checkout-row">
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-zip">ZIP/Postal Code *</label>
            <input type="text" id="ks-zip" class="ks-checkout-input" placeholder="10001" required
              value="${state.shipping?.zipCode || ''}" />
          </div>
          <div class="ks-checkout-field">
            <label class="ks-checkout-label" for="ks-country">Country *</label>
            <select id="ks-country" class="ks-checkout-select" required>
              <option value="">Select country</option>
              ${COUNTRIES.map(
                c => `
                <option value="${c.code}" ${state.shipping?.countryCode === c.code ? 'selected' : ''}>
                  ${c.name}
                </option>
              `
              ).join('')}
            </select>
          </div>
        </div>
        <button type="submit" class="ks-checkout-submit" ${state.isLoading ? 'disabled' : ''}>
          ${
            state.isLoading
              ? '<span class="ks-checkout-spinner"></span> Validating...'
              : 'Continue to Payment \u2192'
          }
        </button>
        <button type="button" class="ks-checkout-back" id="back-to-customer">
          \u2190 Back
        </button>
      </form>
    `;
  }

  function renderPaymentProcessing(): string {
    return `
      <div class="ks-payment-processing">
        <div class="ks-payment-processing-spinner"></div>
        <p class="ks-payment-processing-text">Initializing secure payment...</p>
      </div>
    `;
  }

  function attachEventListeners() {
    // Close button
    overlay.querySelector('.ks-checkout-close')?.addEventListener('click', () => {
      closeModal();
      onClose();
    });

    // Backdrop click
    overlay.addEventListener('click', e => {
      if (e.target === overlay) {
        closeModal();
        onClose();
      }
    });

    // Customer form
    const customerForm = overlay.querySelector('#customer-form') as HTMLFormElement | null;
    if (customerForm) {
      customerForm.addEventListener('submit', handleCustomerSubmit);
    }

    // Shipping form
    const shippingForm = overlay.querySelector('#shipping-form') as HTMLFormElement | null;
    if (shippingForm) {
      shippingForm.addEventListener('submit', handleShippingSubmit);
    }

    // Back button
    const backBtn = overlay.querySelector('#back-to-customer');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        state.step = 'customer';
        state.error = null;
        render();
      });
    }
  }

  async function handleCustomerSubmit(e: Event) {
    e.preventDefault();
    if (state.isLoading) return;

    state.error = null;

    const email = (overlay.querySelector('#ks-email') as HTMLInputElement)?.value.trim();
    const firstName = (overlay.querySelector('#ks-first-name') as HTMLInputElement)?.value.trim();
    const lastName = (overlay.querySelector('#ks-last-name') as HTMLInputElement)?.value.trim();
    const phone = (overlay.querySelector('#ks-phone') as HTMLInputElement)?.value.trim();

    // Validate
    if (!email || !email.includes('@')) {
      state.error = 'Please enter a valid email address';
      render();
      return;
    }

    if (!firstName) {
      state.error = 'Please enter your first name';
      render();
      return;
    }

    if (!lastName) {
      state.error = 'Please enter your last name';
      render();
      return;
    }

    if (!phone) {
      state.error = 'Please enter your phone number';
      render();
      return;
    }

    state.customer = { email, firstName, lastName, phone };

    if (requiresShipping) {
      // Phase 1: Early cart abandonment capture for physical products
      state.isLoading = true;
      render();

      try {
        const payload = cartManager!.buildEarlyCapturePayload({
          email,
          name: `${firstName} ${lastName}`,
          phone,
        });

        const earlyCapture = await api.validateCartEarly(payload);

        state.cartUniqueId = earlyCapture.cart_unique_id;
        state.isLoading = false;
        state.step = 'shipping';
        render();
      } catch (error: any) {
        console.error('[Early Capture]', error);
        state.isLoading = false;
        // Continue to shipping even if early capture fails - it's optional
        state.step = 'shipping';
        render();
      }
    } else {
      // Non-physical products: skip validation, go directly to payment
      await proceedToPayment();
    }
  }

  async function handleShippingSubmit(e: Event) {
    e.preventDefault();
    if (state.isLoading) return;

    state.error = null;

    const name = (overlay.querySelector('#ks-ship-name') as HTMLInputElement)?.value.trim();
    const address = (overlay.querySelector('#ks-address') as HTMLInputElement)?.value.trim();
    const city = (overlay.querySelector('#ks-city') as HTMLInputElement)?.value.trim();
    const stateVal = (overlay.querySelector('#ks-state') as HTMLInputElement)?.value.trim();
    const zipCode = (overlay.querySelector('#ks-zip') as HTMLInputElement)?.value.trim();
    const countryCode = (overlay.querySelector('#ks-country') as HTMLSelectElement)?.value;
    const country = COUNTRIES.find(c => c.code === countryCode)?.name || '';

    // Validate
    if (!name || !address || !city || !stateVal || !zipCode || !countryCode) {
      state.error = 'Please fill in all required fields';
      render();
      return;
    }

    state.shipping = { name, address, city, state: stateVal, zipCode, country, countryCode };
    state.isLoading = true;
    render();

    try {
      // Phase 2: Full cart validation with shipping (strict mode)
      const payload = cartManager!.buildFullValidationPayload(
        {
          name,
          address,
          city,
          state: stateVal,
          zipCode,
          country,
          countryCode,
        },
        {
          email: state.customer?.email,
          phone: state.customer?.phone,
        },
        state.cartUniqueId || undefined
      );

      const validation = await api.validateShippingAndDiscounts(payload);

      state.cartUniqueId = validation.cart_unique_id;
      state.shippingFee = validation.shipping_fee || 0;
      state.totalWithShipping =
        validation.pricing_summary?.cart_final_total ||
        validation.total_sum_with_shipping_fee ||
        subtotal + state.shippingFee;
      state.isLoading = false;

      // Shipping changes the base amount fees are calculated on — refresh.
      refreshTransactionFee();

      // Check for out of stock items
      if (validation.out_of_stock && validation.out_of_stock_items?.length) {
        const outOfStockNames = validation.out_of_stock_items.map(i => i.title).join(', ');
        state.error = `Some items are out of stock: ${outOfStockNames}`;
        render();
        return;
      }

      await proceedToPayment();
    } catch (error: any) {
      console.error('[Shipping Validation]', error);
      state.isLoading = false;
      state.error = error.message || 'Failed to validate shipping. Please try again.';
      render();
    }
  }

  /**
   * Fetch Khaime's transaction fee for the current subtotal+shipping and
   * refresh the summary. Silent no-op on error — the fee row just won't
   * appear and the charge flow still works (the server grosses up anyway).
   */
  async function refreshTransactionFee() {
    try {
      const baseAmount = state.totalWithShipping || subtotal;
      if (!baseAmount || baseAmount <= 0) return;

      const customerCountry = state.shipping?.countryCode || undefined;
      const breakdown = await api.getFeeBreakdown(baseAmount, displayCurrency, customerCountry);
      if (!breakdown) return;

      state.customerPaysFees = breakdown.customerPaysFees;
      state.transactionFee = breakdown.transactionFeeMinor;
      render();
    } catch (err) {
      // Non-fatal — leave previous state in place.
      console.warn('[Khaime] fee breakdown fetch failed:', err);
    }
  }

  async function proceedToPayment() {
    state.step = 'payment';
    state.isLoading = true;
    render();

    try {
      // Sync cart to Redis (skip in single-product mode — no cart to sync)
      if (!isSingleProductMode) {
        await api.syncCart(
          cartId,
          items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            variant_id: cartLineVariantIds(item).variant_id,
            sub_variant_id: cartLineVariantIds(item).sub_variant_id,
          })),
          currency
        );
      }

      let paymentResult: {
        secret?: string;
        publishable_key?: string;
        stripe_account_id?: string;
        access_code?: string;
        reference?: string;
        amount_to_pay: number;
        currency: string;
        payment_gateway: 'stripe' | 'paystack';
      };

      console.log('[Payment Init] Creating payment intent...');
      console.log({ requiresShipping, cartUniqueId: state.cartUniqueId, productType });

      // Use appropriate endpoint based on product type
      if (requiresShipping && state.cartUniqueId) {
        const payload = {
          product_id: items[0].product_id,
          product_title: items.length === 1 ? items[0].title : `Cart (${items.length} items)`,
          product_type: 'physical_product',
          currency,
          order_total_charge: state.totalWithShipping, // Already in cents from validation
          cart_unique_id: state.cartUniqueId,
          cart: items.map(item => {
            const hasVariation = !!item.variant_id;
            const image = typeof item.thumbnail === 'string' ? item.thumbnail : '';
            const base: Record<string, unknown> = {
              product_id: item.product_id,
              quantity: item.quantity,
              has_variation: hasVariation,
              price: item.price,
              product_image: image,
              product_variant_data: hasVariation
                ? (item.variant_name || `Quantity: ${item.quantity}`)
                : `Quantity: ${item.quantity}`,
              product_title: item.title,
              product_thumbnail: image,
            };
            if (hasVariation) {
              const ids = cartLineVariantIds(item);
              base.main_variant = ids.main_variant;
              base.least_sub_variant_id = ids.least_sub_variant_id;
            }
            if (item.subscription_frequency_key) {
              base.subscription_frequency_key = item.subscription_frequency_key;
            }
            return base;
          }),
          delivery_details: {
            save_for_future_checkout: false,
            store_pick_up: false,
            national_delivery: state.shipping!.countryCode === 'US',
            international_delivery: state.shipping!.countryCode !== 'US',
            address: [
              {
                name: state.shipping!.name,
                address: state.shipping!.address,
                city: state.shipping!.city,
                state: state.shipping!.state,
                zip_code: state.shipping!.zipCode,
                country: state.shipping!.country,
                country_code: state.shipping!.countryCode,
                email: state.customer!.email,
                phone_number: state.customer!.phone || '',
              },
            ],
          },
          customer: {
            email: state.customer!.email,
            first_name: state.customer!.firstName,
            last_name: state.customer!.lastName,
            phone: state.customer!.phone || '',
          },
          coupons: [],
        };
        paymentResult = await api.createCartPaymentIntent(payload as any);
      } else {
        // Digital/event/subscription products - use partner payment intent endpoint
        const intentResult = await api.createPaymentIntent({
          amount: state.totalWithShipping, // Amount in cents (backend expects cents)
          currency,
          customer: {
            email: state.customer!.email,
            first_name: state.customer!.firstName,
            last_name: state.customer!.lastName,
            country: state.shipping?.countryCode,
          },
          product_type: (productType as any) || 'digital',
          product_id: items[0]?.product_id,
          product_name: items.length === 1 ? items[0].title : `Cart (${items.length} items)`,
          cart_unique_id: state.cartUniqueId || cartId,
          subscription_frequency_key: items[0]?.subscription_frequency_key,
          tickets: items[0]?.tickets,
          metadata: {
            cart_id: cartId,
            item_count: itemCount,
          },
        });

        // Map partner response to unified format
        paymentResult = {
          secret: intentResult.client_secret,
          publishable_key: intentResult.publishable_key,
          stripe_account_id: intentResult.stripe_account_id,
          access_code: intentResult.access_code,
          amount_to_pay: intentResult.amount,
          currency: intentResult.currency,
          payment_gateway: intentResult.payment_gateway,
        };
      }

      // Open payment modal based on gateway
      const { openCheckoutModal } = await import('./checkout-modal');

      openCheckoutModal({
        merchantId,
        sessionToken: paymentResult.reference || paymentResult.secret?.split('_secret_')[0] || '',
        paymentGateway: paymentResult.payment_gateway,
        clientSecret: paymentResult.secret,
        publishableKey: paymentResult.publishable_key,
        stripeAccountId: paymentResult.stripe_account_id || (paymentResult as any).data?.stripe_account_id,
        accessCode: paymentResult.access_code,
        authorizationUrl: undefined,
        amount: paymentResult.amount_to_pay / 100, // Convert cents to dollars for display
        currency: paymentResult.currency,
        onSuccess: result => {
          closeModal();
          cartManager?.clearCart();
          onSuccess({
            sessionToken: result.sessionToken,
            orderId: result.orderId,
            amount: result.amount,
            currency: result.currency,
            paymentMethod: paymentResult.payment_gateway,
          });
        },
        onError: error => {
          closeModal();
          onError(error);
        },
        onClose: () => {
          closeModal();
          onClose();
        },
      });
    } catch (error: any) {
      console.error('[Payment Init]', error);
      state.isLoading = false;
      state.step = requiresShipping ? 'shipping' : 'customer';
      state.error = error.message || 'Failed to initialize payment. Please try again.';
      render();
    }
  }

  function closeModal() {
    overlay.classList.remove('ks-visible');
    setTimeout(() => {
      overlay.remove();
    }, 200);
  }

  // Initial render
  document.body.appendChild(overlay);
  render();

  // Fire-and-forget fee fetch so the summary picks up the Khaime transaction
  // fee row without blocking the modal open. Re-fetched after shipping.
  refreshTransactionFee();

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('ks-visible');
  });

  // Escape key handler
  const escapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      document.removeEventListener('keydown', escapeHandler);
      closeModal();
      onClose();
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Focus first input
  setTimeout(() => {
    const firstInput = overlay.querySelector('input') as HTMLInputElement | null;
    firstInput?.focus();
  }, 100);
}

export default openCartCheckoutModal;
